import { useState, useCallback, useMemo } from "react";
import { getDefaultNormalizedFields } from "@/utils/normalized-model-fields";

export interface MentionField {
  key: string;
  type: string;
}

export interface MentionState {
  isOpen: boolean;
  query: string;
  startIndex: number;
  position: { top: number; left: number };
  highlightedIndex: number;
}

export interface UseMentionAutocompleteOptions {
  /** List of available fields for mention. Uses default normalized fields if not provided */
  availableFields?: MentionField[];
  /** Trigger character for mention (default: "@") */
  triggerChar?: string;
  /** Callback when a field is selected */
  onSelect?: (field: MentionField) => void;
}

export interface UseMentionAutocompleteReturn {
  /** Current mention state */
  mentionState: MentionState;
  /** Filtered fields based on current query */
  filteredFields: MentionField[];
  /** Handle input change to detect trigger */
  handleInputChange: (
    value: string,
    cursorPosition: number,
    textareaEl: HTMLTextAreaElement | null
  ) => void;
  /** Handle keyboard navigation */
  handleKeyDown: (e: React.KeyboardEvent) => boolean;
  /** Select a field from the list */
  selectField: (field: MentionField, currentValue: string) => string;
  /** Close the mention popover */
  closeMention: () => void;
  /** Set highlighted index */
  setHighlightedIndex: (index: number) => void;
}

const initialState: MentionState = {
  isOpen: false,
  query: "",
  startIndex: -1,
  position: { top: 0, left: 0 },
  highlightedIndex: 0,
};

// Characters that should stop mention detection (Lucene syntax chars + whitespace)
const STOP_CHARS = new Set([" ", "\n", "\t", ":", "(", ")", "[", "]", "{", "}", '"', "'"]);

/**
 * Hook for managing "@" mention autocomplete in text inputs
 *
 * Features:
 * - Detects "@" trigger character
 * - Filters available fields based on typed query
 * - Keyboard navigation (ArrowUp/Down, Enter, Escape)
 * - Calculates popover position based on cursor
 */
export const useMentionAutocomplete = (
  options: UseMentionAutocompleteOptions = {}
): UseMentionAutocompleteReturn => {
  const {
    availableFields,
    triggerChar = "@",
    onSelect,
  } = options;

  const [mentionState, setMentionState] = useState<MentionState>(initialState);

  // Use default normalized fields if none provided
  const fields = useMemo(() => {
    if (availableFields && availableFields.length > 0) {
      return availableFields;
    }
    return getDefaultNormalizedFields();
  }, [availableFields]);

  // Filter fields based on current query
  const filteredFields = useMemo(() => {
    if (!mentionState.query) {
      return fields;
    }
    const lowerQuery = mentionState.query.toLowerCase();
    return fields.filter((field) =>
      field.key.toLowerCase().includes(lowerQuery)
    );
  }, [fields, mentionState.query]);

  // Calculate cursor position using Selection API (works for both textarea and contenteditable)
  const getCursorPositionFromSelection = useCallback((): { top: number; left: number } => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return { top: 0, left: 0 };
    }
    
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    
    // If rect is empty (e.g., at start of element), try getting parent rect
    if (rect.width === 0 && rect.height === 0) {
      const parentEl = range.startContainer.parentElement;
      if (parentEl) {
        const parentRect = parentEl.getBoundingClientRect();
        return { top: parentRect.bottom, left: parentRect.left };
      }
    }
    
    return { top: rect.bottom, left: rect.left };
  }, []);

  // Handle input change to detect trigger
  // textareaEl can be null for contenteditable (we use Selection API instead)
  const handleInputChange = useCallback(
    (
      value: string,
      cursorPosition: number,
      _textareaEl: HTMLTextAreaElement | null
    ) => {
      // Find the start of the current mention (if any)
      let mentionStart = -1;
      let query = "";

      // Look backwards from cursor to find "@"
      for (let i = cursorPosition - 1; i >= 0; i--) {
        const char = value[i];
        
        // Stop if we hit a stop character (space, newline, Lucene syntax chars)
        if (STOP_CHARS.has(char)) {
          break;
        }
        
        // Found the trigger character
        if (char === triggerChar) {
          mentionStart = i;
          query = value.substring(i + 1, cursorPosition);
          break;
        }
      }

      if (mentionStart >= 0) {
        // Use Selection API to get cursor position (works for both textarea and contenteditable)
        const position = getCursorPositionFromSelection();
        setMentionState({
          isOpen: true,
          query,
          startIndex: mentionStart,
          position,
          highlightedIndex: 0,
        });
      } else {
        if (mentionState.isOpen) {
          setMentionState(initialState);
        }
      }
    },
    [triggerChar, getCursorPositionFromSelection, mentionState.isOpen]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): boolean => {
      if (!mentionState.isOpen) {
        return false;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setMentionState((prev) => ({
            ...prev,
            highlightedIndex: Math.min(
              prev.highlightedIndex + 1,
              Math.max(filteredFields.length - 1, 0)
            ),
          }));
          return true;

        case "ArrowUp":
          e.preventDefault();
          setMentionState((prev) => ({
            ...prev,
            highlightedIndex: Math.max(prev.highlightedIndex - 1, 0),
          }));
          return true;

        case "Enter":
        case "Tab":
          if (filteredFields.length > 0) {
            e.preventDefault();
            return true; // Signal that selection should happen
          }
          return false;

        case "Escape":
          e.preventDefault();
          setMentionState(initialState);
          return true;

        default:
          return false;
      }
    },
    [mentionState.isOpen, filteredFields.length]
  );

  // Select a field and return the new value
  // Note: This now inserts the field.key directly (display value)
  // The conversion to internal format (with prefix) is handled by the component
  const selectField = useCallback(
    (field: MentionField, currentValue: string): string => {
      const { startIndex, query } = mentionState;
      
      // Replace @query with field key (display name)
      const beforeMention = currentValue.substring(0, startIndex);
      const afterMention = currentValue.substring(startIndex + 1 + query.length);
      const newValue = beforeMention + field.key + afterMention;

      onSelect?.(field);
      setMentionState(initialState);

      return newValue;
    },
    [mentionState, onSelect]
  );

  // Close the mention popover
  const closeMention = useCallback(() => {
    setMentionState(initialState);
  }, []);

  // Set highlighted index
  const setHighlightedIndex = useCallback((index: number) => {
    setMentionState((prev) => ({
      ...prev,
      highlightedIndex: index,
    }));
  }, []);

  return {
    mentionState,
    filteredFields,
    handleInputChange,
    handleKeyDown,
    selectField,
    closeMention,
    setHighlightedIndex,
  };
};

export default useMentionAutocomplete;
